import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { FolderOpen, FileCheck, FileClock, Receipt, Users, TrendingUp, Calendar as CalendarIcon, Plus, Clock, MapPin, Trash2, CheckCircle, XCircle, ExternalLink, Briefcase, FileText, User, Eye, Archive, ListTodo, Activity, Phone, AlertTriangle, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import Calendar from 'react-calendar';
import ScheduleSection from '@/components/ScheduleSection';
import StatsCards from '@/components/StatsCards';
import EmployeeTasks from '@/components/EmployeeTasks';
import DeadlineStatsWidget from '@/components/DeadlineStatsWidget';
import DashboardFilesList from '@/components/dashboard/DashboardFilesList';
import DashboardMeetings from '@/components/dashboard/DashboardMeetings';
import FormsTrackingWidget from '@/components/dashboard/FormsTrackingWidget';
import sanitize from '@/utils/sanitize';
import { getApiUrl } from '@/utils/apiUrl';

const Dashboard = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingDocsCount, setPendingDocsCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [fileStatusTab, setFileStatusTab] = useState('actif');
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [schedule, setSchedule] = useState(null);
  const [deadlineStats, setDeadlineStats] = useState(null);
  const [orgInfo, setOrgInfo] = useState(null);
  const [irccAlerts, setIrccAlerts] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    participant_ids: [],
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    duration: 60,
    location: '',
    meeting_type: 'employee'
  });

  
  const API = getApiUrl();

  // Memoized expensive computations
  const activeTaskCount = useMemo(() => tasks.filter(t => t.status !== 'termine').length, [tasks]);
  const actifFileCount = useMemo(() => files.filter(f => ['ouvert', 'en_cours', 'en_traitement', 'soumission_ircc'].includes(f.status)).length, [files]);
  const closFileCount = useMemo(() => files.filter(f => f.status === 'ferme').length, [files]);
  const nouveauFileCount = useMemo(() => files.filter(f => f.status === 'nouveau').length, [files]);
  const recentFiles = useMemo(() => filteredFiles.slice(0, 6), [filteredFiles]);

  useEffect(() => {
    // ✅ AbortController pour cleanup automatique
    const abortController = new AbortController();
    let isMounted = true;

    const loadAllData = async () => {
      const signal = abortController.signal;
      const mountedRef = { current: isMounted };
      let dataLoaded = false;
      // Try combined endpoint first (fast)
      try {
        const dashResponse = await axios.get(`${API}/dashboard/all`, {
          headers: { Authorization: `Bearer ${token}` },
          signal,
          timeout: 8000,
        });
        if (!isMounted) return;
        const data = dashResponse.data;
        if (data.stats) setStats(data.stats);
        if (data.schedule) setSchedule(data.schedule);
        if (data.schedule?.tasks) setTasks(data.schedule.tasks);
        dataLoaded = true;
      } catch (err) {
        // Combined endpoint failed — not critical
        if (err?.cancelled || err?.name === 'AbortError') return;
      }
      // Always fetch files + users (needed for all views)
      try {
        const promises = [fetchFiles(signal, mountedRef)];
        if (!dataLoaded) {
          promises.push(fetchStats(signal, mountedRef));
          promises.push(fetchSchedule(signal, mountedRef));
        }
        if (user?.role === 'admin' || user?.role === 'employee') {
          promises.push(fetchUsers(signal, mountedRef));
        }
        if (user?.role === 'admin') {
          promises.push(fetchPendingDocuments(signal, mountedRef));
          promises.push(fetchDeadlineStats(signal, mountedRef));
        }
        // Fetch org info for org_admin dashboard branding
        if (user?.role === 'org_admin' || user?.role === 'org_employee') {
          promises.push(
            axios.get(`${API}/my-organization`, { headers: { Authorization: `Bearer ${token}` }, signal })
              .then(res => { if (mountedRef.current) setOrgInfo(res.data); })
              .catch(() => {})
          );
        }
        // Fetch IRCC alerts for staff
        if (user?.role !== 'client') {
          promises.push(
            axios.get(`${API}/ircc/dashboard`, { headers: { Authorization: `Bearer ${token}` }, signal })
              .then(res => { if (mountedRef.current) setIrccAlerts(res.data); })
              .catch(() => {})
          );
        }
        await Promise.allSettled(promises);
      } catch (err) {
        /* silenced */
      }
      // ALWAYS stop loading
      if (isMounted) setLoading(false);
    };

    loadAllData();

    // ✅ Cleanup: annuler toutes les requetes lors du demontage
    return () => {
      isMounted = false;
      abortController.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, token]);

  const fetchStats = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
        timeout: 10000 // 10 second timeout
      });
      if (isMountedRef.current && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      // Only show error if it's not a cancellation
      if (error.name !== 'AbortError' && error.name !== 'CanceledError' && !error?.cancelled && !axios.isCancel(error)) {
        // Don't reset stats to 0 on error - keep existing values or leave as null
        // This prevents the "flashing 0" problem when requests are cancelled
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchMeetings = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/meetings`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setMeetings(response.data);
      }
    } catch (error) {
      /* silenced */
    }
  };

  const fetchUsers = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setUsers(response.data);
      }
    } catch (error) {
      /* silenced */
    }
  };

  const fetchFiles = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/files`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setFiles(response.data);
      }
    } catch (error) {
      /* silenced */
    }
  };

  const fetchTasks = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setTasks(response.data || []);
      }
    } catch (error) {
      /* silenced */
    }
  };

  const handleToggleTaskStatus = async (taskId, currentStatus) => {
    const newStatus = currentStatus === 'termine' ? 'en_cours' : 'termine';
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Statut de la tache mis a jour');
      fetchTasks();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const fetchSchedule = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/schedule/today`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setSchedule(response.data);
      }
    } catch (error) {
      /* silenced */
    }
  };

  const fetchDeadlineStats = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/deadline/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setDeadlineStats(response.data);
      }
    } catch (error) {
      /* silenced */
    }
  };

  useEffect(() => {
    filterFilesByStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, fileStatusTab]);

  const filterFilesByStatus = () => {
    let filtered;
    if (fileStatusTab === 'nouveau') {
      filtered = files.filter(f => f.status === 'nouveau');
    } else if (fileStatusTab === 'actif') {
      filtered = files.filter(f => ['ouvert', 'en_cours', 'en_traitement', 'soumission_ircc'].includes(f.status));
    } else {
      filtered = files.filter(f => f.status === 'ferme');
    }
    setFilteredFiles(filtered);
  };


  const fetchPendingDocuments = async (signal, isMountedRef = { current: true }) => {
    try {
      const response = await axios.get(`${API}/documents/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` },
        signal
      });
      if (isMountedRef.current) {
        setPendingDocsCount(response.data.length);
      }
    } catch (error) {
      /* silenced */
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/meetings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meeting cree avec succes');
      setDialogOpen(false);
      resetForm();
      fetchMeetings();
    } catch (error) {
      toast.error('Erreur lors de la creation du meeting');
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Supprimer ce meeting ?')) return;

    try {
      await axios.delete(`${API}/meetings/${meetingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Meeting supprime');
      fetchMeetings();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateStatus = async (meetingId, status) => {
    try {
      await axios.put(
        `${API}/meetings/${meetingId}/status`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { status }
        }
      );
      toast.success('Statut mis a jour');
      fetchMeetings();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      participant_ids: [],
      date: format(new Date(), 'yyyy-MM-dd'),
      time: '09:00',
      duration: 60,
      location: '',
      meeting_type: 'employee'
    });
  };

  const getMeetingsForDate = useCallback((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return meetings.filter(m => m.date === dateStr);
  }, [meetings]);

  const upcomingMeetings = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return meetings
      .filter(m => m.date >= today && m.status === 'scheduled')
      .sort((a, b) => {
        if (a.date === b.date) return a.time.localeCompare(b.time);
        return a.date.localeCompare(b.date);
      })
      .slice(0, 5);
  }, [meetings]);

  const getStatusBadge = (status) => {
    const badges = {
      scheduled: <Badge className="bg-blue-100 text-blue-800">Programme</Badge>,
      completed: <Badge className="bg-green-100 text-green-800">Termine</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-800">Annule</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const statCards = [
    {
      title: 'Nouveaux dossiers',
      value: stats?.nouveau_files || 0,
      icon: FolderOpen,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'bg-indigo-50',
      href: '/files'
    },
    {
      title: 'Dossiers actifs',
      value: stats?.active_files || 0,
      icon: FileClock,
      color: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      href: '/files'
    },
    {
      title: 'Clients individuels',
      value: stats?.total_clients || 0,
      icon: Users,
      color: 'from-rose-500 to-rose-600',
      bgColor: 'bg-rose-50',
      href: '/clients'
    },
    {
      title: 'Groupes',
      value: stats?.total_groups || 0,
      icon: Users,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      href: '/groupes'
    },
    {
      title: 'Dossiers fermes',
      value: stats?.closed_files || 0,
      icon: FileCheck,
      color: 'from-emerald-500 to-green-600',
      bgColor: 'bg-emerald-50',
      href: '/files'
    },
    {
      title: 'Total dossiers',
      value: stats?.total_files || 0,
      icon: Briefcase,
      color: 'from-gray-500 to-gray-600',
      bgColor: 'bg-gray-50',
      href: '/files'
    }
  ];

  if (user?.role === 'admin' || user?.role === 'org_admin') {
    statCards.push({
      title: 'Employes',
      value: stats?.total_employees || 0,
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50'
    });
  }

  if (user?.role === 'employer') return <Navigate to="/portail-employeur" replace />;
  if (user?.role === 'candidate') return <Navigate to="/portail-candidat" replace />;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-container">
        {/* Organization branding header */}
        {orgInfo && (
          <div className="flex items-center gap-4 p-4 rounded-xl border-2" style={{ borderColor: orgInfo.primary_color || '#C0392B', backgroundColor: `${orgInfo.primary_color || '#C0392B'}08` }}>
            {orgInfo.logo_url ? (
              <img src={orgInfo.logo_url} alt="" className="h-14 w-auto rounded-lg" />
            ) : (
              <div className="h-14 w-14 rounded-lg flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: orgInfo.primary_color || '#C0392B' }}>
                {(orgInfo.name || 'O').charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold" style={{ color: orgInfo.primary_color || '#C0392B' }}>{orgInfo.name}</h2>
              {orgInfo.slogan && <p className="text-xs text-gray-500">{orgInfo.slogan}</p>}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              {t('welcome')}, {user?.full_name}
            </h1>
            <p className="text-lg text-gray-600">{t('dashboard')} - {t(user?.role)}</p>
          </div>
          {(user?.role === 'admin' || user?.is_super_admin) && (
            <Button onClick={() => navigate('/rapprochement-cc')}
              className="bg-red-600 hover:bg-red-700 font-bold gap-2" data-testid="dashboard-recon-btn">
              <Wallet className="h-4 w-4" /> RAPPROCHEMENT CC
            </Button>
          )}
        </div>

        {/* ═══ SCHEDULE D'AUJOURD'HUI ═══ */}
        {(user?.role === 'employee' || user?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'org_employee') && schedule && (
          <ScheduleSection schedule={schedule} />
        )}

        {/* Onglets pour Employe */}
        {user?.role === 'employee' && (
          <div className="flex space-x-2 bg-gradient-to-r from-orange-100 to-red-100 p-1.5 rounded-lg w-fit shadow-lg border-2 border-orange-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'bg-white text-orange-600 shadow-md'
                  : 'text-gray-700 hover:bg-white/50'
              }`}
            >
              <Briefcase className="h-5 w-5" />
              {`Vue d'ensemble`}
            </button>
            <button
              onClick={() => setActiveTab('tasks')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 ${
                activeTab === 'tasks'
                  ? 'bg-white text-orange-600 shadow-md'
                  : 'text-gray-700 hover:bg-white/50'
              }`}
            >
              <ListTodo className="h-5 w-5" />
              Taches a faire
              {tasks.length > 0 && (
                <Badge className="bg-orange-600 text-white">
                  {activeTaskCount}
                </Badge>
              )}
            </button>
          </div>
        )}

        {/* Contenu base sur l'onglet actif pour employe */}
        {user?.role === 'employee' && activeTab === 'tasks' ? (
          <EmployeeTasks tasks={tasks} onToggleStatus={handleToggleTaskStatus} />
        ) : (
          <>
            {/* Vue d'ensemble normale */}

        {/* Stats Cards */}
        <StatsCards cards={statCards} />

        {/* CRITICAL IRCC BAR — Blinking alert at top of dashboard */}
        {irccAlerts && user?.role !== 'client' && (irccAlerts.ircc_requests?.overdue > 0 || irccAlerts.deadlines?.overdue > 0 || irccAlerts.deadlines?.today > 0) && (
          <div className="animate-pulse bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl p-4 shadow-xl border-2 border-red-400" data-testid="ircc-critical-bar">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg"><AlertTriangle className="h-6 w-6" /></div>
                <div>
                  <h3 className="font-black text-lg">ALERTE IRCC CRITIQUE</h3>
                  <p className="text-red-100 text-sm">
                    {irccAlerts.deadlines.overdue > 0 && `${irccAlerts.deadlines.overdue} echeance(s) depassee(s)`}
                    {irccAlerts.deadlines.overdue > 0 && irccAlerts.deadlines.today > 0 && ' — '}
                    {irccAlerts.deadlines.today > 0 && `${irccAlerts.deadlines.today} echeance(s) aujourd'hui`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                  <p className="text-2xl font-black">{irccAlerts.deadlines.overdue + irccAlerts.deadlines.today}</p>
                  <p className="text-[10px] text-red-200">Critiques</p>
                </div>
                <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                  <p className="text-2xl font-black">{irccAlerts.submissions_to_verify}</p>
                  <p className="text-[10px] text-red-200">A verifier</p>
                </div>
                <Button onClick={() => navigate('/ircc-control')} className="bg-white text-red-700 hover:bg-red-50 font-bold">
                  Traiter maintenant
                </Button>
              </div>
            </div>
            {irccAlerts.critical_alerts?.slice(0, 3).map(alert => (
              <div key={alert.req_id} className="mt-2 bg-white/10 rounded-lg p-2 flex justify-between items-center">
                <div className="text-sm">
                  <span className="font-bold">{alert.file_number}</span> — {alert.client_name} — <span className="text-red-200">{alert.document} ({alert.days_overdue}j retard)</span>
                </div>
                <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={() => navigate('/ircc-control')}>Ouvrir</Button>
              </div>
            ))}
          </div>
        )}

        {/* IRCC Non-critical alerts */}
        {irccAlerts && user?.role !== 'client' && irccAlerts.deadlines?.overdue === 0 && irccAlerts.deadlines?.today === 0 && (irccAlerts.deadlines?.['3_days'] > 0 || irccAlerts.submissions_to_verify > 0) && (
          <Card className="border-2 border-amber-300 bg-amber-50 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <div>
                    <h3 className="font-bold text-amber-800">Alertes IRCC</h3>
                    <p className="text-xs text-amber-600">{irccAlerts.deadlines['3_days'] + irccAlerts.deadlines['7_days']} echeance(s) cette semaine — {irccAlerts.submissions_to_verify} soumission(s) a verifier</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigate('/ircc-control')} className="border-amber-400 text-amber-700">Voir</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deadline Statistics - Admin Only */}
        {user?.role === 'admin' && (
          <DeadlineStatsWidget deadlineStats={deadlineStats} />
        )}

        {/* Suivi des formulaires manquants avant soumission */}
        {(user?.role === 'admin' || user?.role === 'employee' || user?.role === 'org_admin' || user?.role === 'org_employee') && (
          <FormsTrackingWidget token={token} />
        )}


        {/* Dossiers Actifs/Clos Tabs Section */}
        {(user?.role === 'admin' || user?.role === 'employee') && (
          <DashboardFilesList
            fileStatusTab={fileStatusTab}
            setFileStatusTab={setFileStatusTab}
            nouveauFileCount={nouveauFileCount}
            actifFileCount={actifFileCount}
            closFileCount={closFileCount}
            filteredFiles={filteredFiles}
            recentFiles={recentFiles}
          />
        )}

        {/* Documents Pending Approval - Admin Only */}
        {user?.role === 'admin' && pendingDocsCount > 0 && (
          <Card className="border-0 shadow-xl bg-gradient-to-br from-yellow-50 to-orange-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-yellow-600 to-orange-600">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {pendingDocsCount} Document{pendingDocsCount > 1 ? 's' : ''} {`en Attente d'Approbation`}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Des documents generes necessitent votre validation
                    </p>
                  </div>
                </div>
                <a href="/document-approvals">
                  <Button className="bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700">
                    <CheckCircle className="mr-2 h-5 w-5" />
                    Voir les documents
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Outils de Gestion Integres - Admin & Employee */}
        {(user?.role === 'admin' || user?.role === 'employee') && (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Briefcase className="h-6 w-6 text-red-600" />
              Outils de Gestion
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 bg-gradient-to-br from-green-50 to-blue-50 cursor-pointer"
                onClick={() => window.location.href = '/workflow-tracker'}>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-gradient-to-br from-green-600 to-blue-600">
                        <TrendingUp className="h-8 w-8 text-white" />
                      </div>
                      <Badge className="bg-green-500 text-white">Integre</Badge>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        Suivi Workflow
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Suivi en temps reel de la progression de tous les dossiers clients avec visualisation par etapes
                      </p>
                    </div>
                    <Button className="w-full bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700">
                      <TrendingUp className="mr-2 h-5 w-5" />
                      Acceder au suivi
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Agenda Section */}
        {(user?.role === 'admin' || user?.role === 'employee') && (
          <DashboardMeetings
            meetings={meetings}
            users={users}
            user={user}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            getMeetingsForDate={getMeetingsForDate}
            upcomingMeetings={upcomingMeetings}
            onCreateMeeting={handleCreateMeeting}
            onUpdateStatus={handleUpdateStatus}
            onDeleteMeeting={handleDeleteMeeting}
            dialogOpen={dialogOpen}
            setDialogOpen={setDialogOpen}
            formData={formData}
            setFormData={setFormData}
          />
        )}

        {/* Statistiques Section */}
        <Card className="border-0 shadow-xl">
          <CardHeader>
            <CardTitle>Statistiques</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-gray-500">
              <p>Les graphiques et activites recentes seront affiches ici</p>
            </div>
          </CardContent>
        </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
